import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Shield,
  Scan,
  Link2,
  Eye,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  X,
  Camera,
  Lock,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface MicrochipLinkingProps {
  petId: string;
  petName: string;
  hasDID: boolean;
  onComplete: (chipData: MicrochipData) => void;
  onCancel: () => void;
}

export interface MicrochipData {
  chipNumber: string;
  standard?: 'ISO' | 'AVID' | 'Other';
  implantDate?: string;
  implantLocation?: string;
  linkToDID: boolean;
  visibility: 'only-me' | 'professionals' | 'emergency';
}

type FlowStep =
  | 'education'
  | 'enter-details'
  | 'link-did'
  | 'visibility'
  | 'confirmation';

export function MicrochipLinking({
  petId: _petId,
  petName,
  hasDID,
  onComplete,
  onCancel,
}: MicrochipLinkingProps) {
  const { i18n } = useTranslation();
  const isItalian = (i18n.resolvedLanguage || i18n.language || '')
    .toLowerCase()
    .startsWith('it');
  const tr = (en: string, it: string) => (isItalian ? it : en);
  const [currentStep, setCurrentStep] = useState<FlowStep>('education');
  const [understood, setUnderstood] = useState(false);
  const [chipData, setChipData] = useState<Partial<MicrochipData>>({
    linkToDID: hasDID,
    visibility: 'only-me',
  });
  const [showScanner, setShowScanner] = useState(false);
  const [isAnimated, setIsAnimated] = useState(false);

  useEffect(() => {
    const t = requestAnimationFrame(() => setIsAnimated(true));
    return () => cancelAnimationFrame(t);
  }, []);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const updateChipData = <K extends keyof MicrochipData>(
    key: K,
    value: MicrochipData[K]
  ) => {
    setChipData((prev) => ({ ...prev, [key]: value }));
  };

  const validateChipNumber = (number: string): boolean => {
    // Basic validation: 9-15 digits
    const cleaned = number.replace(/\s/g, '');
    return /^\d{9,15}$/.test(cleaned);
  };

  const canProceedFromDetails = () => {
    return chipData.chipNumber && validateChipNumber(chipData.chipNumber);
  };

  const handleComplete = () => {
    if (chipData.chipNumber && chipData.visibility) {
      onComplete(chipData as MicrochipData);
    }
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop overlay: rendered in portal so it always covers viewport */}
      <div
        className="absolute inset-0"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.65)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
        }}
        aria-hidden="true"
      />
      <div
        className={`relative z-10 bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto overscroll-contain rounded-3xl border border-gray-200/80 transition-all duration-200 ease-out ${
          isAnimated ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        style={{
          boxShadow:
            '0 25px 50px -12px rgba(0,0,0,0.18), 0 12px 24px -8px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.04), 0 0 40px -10px rgba(99,102,241,0.15)',
        }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <Scan className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2
                className="text-2xl"
                style={{
                  color: '#2D2F7E',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: 700,
                }}
              >
                {tr('Link Microchip', 'Collega microchip')}
              </h2>
              <p
                className="text-sm"
                style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}
              >
                {isItalian ? `Per ${petName}` : `For ${petName}`}
              </p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5" style={{ color: '#464452' }} />
          </button>
        </div>

        {/* Progress Indicator */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between max-w-md mx-auto">
            {[
              'education',
              'enter-details',
              hasDID ? 'link-did' : null,
              'visibility',
              'confirmation',
            ]
              .filter(Boolean)
              .map((step, index, arr) => (
                <div key={step} className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                      arr.indexOf(currentStep) >= index
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {index + 1}
                  </div>
                  {index < arr.length - 1 && (
                    <div
                      className={`w-12 h-1 mx-1 transition-colors ${
                        arr.indexOf(currentStep) > index
                          ? 'bg-blue-600'
                          : 'bg-gray-200'
                      }`}
                    />
                  )}
                </div>
              ))}
          </div>
        </div>

        <div className="p-6">
          {/* Step 1: Education & Consent */}
          {currentStep === 'education' && (
            <div className="space-y-6">
              <div>
                <h3
                  className="text-xl mb-2"
                  style={{
                    color: '#2D2F7E',
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 700,
                  }}
                >
                  {tr(
                    "Link Your Pet's Microchip (Optional)",
                    'Collega il microchip del tuo animale (opzionale)'
                  )}
                </h3>
                <p
                  style={{
                    color: '#464452',
                    fontFamily: 'Inter, sans-serif',
                    lineHeight: '1.6',
                  }}
                >
                  {tr(
                    `If your pet has a microchip, you can optionally link its identification number to your Petso profile${hasDID ? ' and Pet DID' : ''}. This does not replace your microchip registry and does not share your pet's records automatically.`,
                    `Se il tuo animale ha un microchip, puoi collegare opzionalmente il numero identificativo al profilo Petso${hasDID ? ' e al DID' : ''}. Questo non sostituisce il registro microchip ufficiale e non condivide automaticamente i record.`
                  )}
                </p>
              </div>

              {/* What this does */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-5">
                <div className="flex items-start gap-3 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4
                      className="font-semibold mb-2"
                      style={{
                        color: '#2D2F7E',
                        fontFamily: 'Sora, sans-serif',
                      }}
                    >
                      {tr('What this DOES:', 'Cosa FA:')}
                    </h4>
                    <ul
                      className="space-y-1 text-sm"
                      style={{
                        color: '#464452',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      <li>
                        •{' '}
                        {tr(
                          'Helps identify pets faster in emergencies',
                          'Aiuta a identificare più rapidamente gli animali nelle emergenze'
                        )}
                      </li>
                      <li>
                        •{' '}
                        {tr(
                          'Links chip number to your pet profile',
                          'Collega il numero microchip al profilo del tuo animale'
                        )}
                      </li>
                      <li>
                        •{' '}
                        {tr(
                          'Adds a recovery reference point',
                          'Aggiunge un punto di riferimento per il recupero'
                        )}
                      </li>
                      <li>
                        •{' '}
                        {tr(
                          'Creates a private, encrypted record',
                          'Crea un record privato e crittografato'
                        )}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* What this does NOT do */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                <div className="flex items-start gap-3 mb-3">
                  <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4
                      className="font-semibold mb-2"
                      style={{
                        color: '#2D2F7E',
                        fontFamily: 'Sora, sans-serif',
                      }}
                    >
                      {tr('What this does NOT do:', 'Cosa NON fa:')}
                    </h4>
                    <ul
                      className="space-y-1 text-sm"
                      style={{
                        color: '#464452',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      <li>
                        •{' '}
                        {tr(
                          'Does not change chip ownership',
                          'Non cambia la proprietà del microchip'
                        )}
                      </li>
                      <li>
                        •{' '}
                        {tr(
                          'Does not replace existing registries',
                          'Non sostituisce i registri esistenti'
                        )}
                      </li>
                      <li>
                        •{' '}
                        {tr(
                          'Does not share medical data automatically',
                          'Non condivide automaticamente dati medici'
                        )}
                      </li>
                      <li>
                        •{' '}
                        {tr(
                          'Does not make chip registration mandatory',
                          'Non rende obbligatoria la registrazione del microchip'
                        )}
                      </li>
                      <li>
                        •{' '}
                        {tr(
                          'Does not modify external databases',
                          'Non modifica database esterni'
                        )}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Important Note */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p
                      className="text-sm"
                      style={{
                        color: '#464452',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      <strong style={{ color: '#2D2F7E' }}>
                        {tr('Important:', 'Importante:')}
                      </strong>{' '}
                      {tr(
                        'Linking a microchip is optional and for identification reference only. Petso does not replace or modify external microchip registries. Always keep your official microchip registry updated.',
                        'Il collegamento del microchip è opzionale ed è solo un riferimento identificativo. Petso non sostituisce né modifica i registri microchip esterni. Mantieni sempre aggiornato il tuo registro microchip ufficiale.'
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Consent Checkbox */}
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                <input
                  type="checkbox"
                  id="understand"
                  checked={understood}
                  onChange={(e) => setUnderstood(e.target.checked)}
                  className="mt-1 w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <label
                  htmlFor="understand"
                  className="flex-1 cursor-pointer"
                  style={{ color: '#2D2F7E', fontFamily: 'Inter, sans-serif' }}
                >
                  {tr(
                    'I understand this is optional and does not replace my official microchip registry',
                    'Capisco che è opzionale e non sostituisce il mio registro microchip ufficiale'
                  )}
                </label>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={onCancel}
                  className="flex-1 px-6 py-3 rounded-xl border-2 border-gray-300 hover:bg-gray-50 transition-colors"
                  style={{
                    color: '#464452',
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 700,
                  }}
                >
                  {tr('Cancel', 'Annulla')}
                </button>
                <button
                  onClick={() => setCurrentStep('enter-details')}
                  disabled={!understood}
                  className="flex-1 px-6 py-3 rounded-xl text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={{
                    background:
                      'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 700,
                  }}
                >
                  {tr('Continue', 'Continua')}
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Enter Microchip Details */}
          {currentStep === 'enter-details' && (
            <div className="space-y-6">
              <div>
                <h3
                  className="text-xl mb-2"
                  style={{
                    color: '#2D2F7E',
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 700,
                  }}
                >
                  {tr(
                    'Enter Microchip Details',
                    'Inserisci i dettagli del microchip'
                  )}
                </h3>
                <p
                  style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}
                >
                  {tr(
                    "Enter your pet's microchip information. You can scan it using your camera or enter it manually.",
                    'Inserisci le informazioni microchip del tuo animale. Puoi scansionarle con la fotocamera o inserirle manualmente.'
                  )}
                </p>
              </div>

              {/* Microchip Number */}
              <div>
                <label
                  className="block mb-2 font-semibold"
                  style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif' }}
                >
                  {tr(
                    'Microchip Identification Number *',
                    'Numero identificativo microchip *'
                  )}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder={tr(
                      'e.g., 123456789012345',
                      'es. 123456789012345'
                    )}
                    value={chipData.chipNumber || ''}
                    onChange={(e) =>
                      updateChipData('chipNumber', e.target.value)
                    }
                    className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:outline-none"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                    maxLength={15}
                  />
                  <button
                    onClick={() => setShowScanner(!showScanner)}
                    className="px-4 py-3 rounded-xl border-2 border-gray-200 hover:bg-gray-50 transition-colors flex items-center gap-2"
                    style={{
                      color: '#464452',
                      fontFamily: 'Sora, sans-serif',
                      fontWeight: 700,
                    }}
                  >
                    <Camera className="w-5 h-5" />
                    {showScanner
                      ? tr('Close', 'Chiudi')
                      : tr('Scan', 'Scansiona')}
                  </button>
                </div>
                {chipData.chipNumber &&
                  !validateChipNumber(chipData.chipNumber) && (
                    <p
                      className="mt-2 text-sm text-red-600"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {tr(
                        'Microchip number should be 9-15 digits',
                        'Il numero del microchip deve contenere 9-15 cifre'
                      )}
                    </p>
                  )}
                {chipData.chipNumber &&
                  validateChipNumber(chipData.chipNumber) && (
                    <p
                      className="mt-2 text-sm text-green-600 flex items-center gap-1"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      <CheckCircle className="w-4 h-4" />
                      {tr('Valid format', 'Formato valido')}
                    </p>
                  )}
              </div>

              {/* Scanner Placeholder */}
              {showScanner && (
                <div className="bg-gray-100 rounded-xl p-8 text-center">
                  <Camera className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p
                    className="text-sm"
                    style={{
                      color: '#464452',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    {tr(
                      'Camera scanner would appear here. In production, this would use OCR to scan the microchip certificate.',
                      'Lo scanner fotocamera apparirebbe qui. In produzione, userebbe OCR per scansionare il certificato del microchip.'
                    )}
                  </p>
                </div>
              )}

              {/* Microchip Standard */}
              <div>
                <label
                  className="block mb-2 font-semibold"
                  style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif' }}
                >
                  {tr(
                    'Microchip Standard (Optional)',
                    'Standard microchip (opzionale)'
                  )}
                </label>
                <select
                  value={chipData.standard || ''}
                  onChange={(e) => updateChipData('standard', e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:outline-none"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <option value="">
                    {tr(
                      'Select standard (if known)',
                      'Seleziona standard (se noto)'
                    )}
                  </option>
                  <option value="ISO">
                    {tr(
                      'ISO 11784/11785 (Most common)',
                      'ISO 11784/11785 (più comune)'
                    )}
                  </option>
                  <option value="AVID">
                    {tr(
                      'AVID (FriendChip/EuroChip)',
                      'AVID (FriendChip/EuroChip)'
                    )}
                  </option>
                  <option value="Other">
                    {tr('Other/Unknown', 'Altro/Sconosciuto')}
                  </option>
                </select>
              </div>

              {/* Implant Date */}
              <div>
                <label
                  className="block mb-2 font-semibold"
                  style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif' }}
                >
                  {tr('Implant Date (Optional)', 'Data impianto (opzionale)')}
                </label>
                <input
                  type="date"
                  value={chipData.implantDate || ''}
                  onChange={(e) =>
                    updateChipData('implantDate', e.target.value)
                  }
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:outline-none"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                />
              </div>

              {/* Implant Location */}
              <div>
                <label
                  className="block mb-2 font-semibold"
                  style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif' }}
                >
                  {tr(
                    'Implant Location (Optional)',
                    'Posizione impianto (opzionale)'
                  )}
                </label>
                <input
                  type="text"
                  placeholder={tr(
                    'e.g., Between shoulder blades',
                    'es. Tra le scapole'
                  )}
                  value={chipData.implantLocation || ''}
                  onChange={(e) =>
                    updateChipData('implantLocation', e.target.value)
                  }
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:outline-none"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                />
              </div>

              {/* No Registry Lookup Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Lock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p
                      className="text-sm"
                      style={{
                        color: '#464452',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      <strong style={{ color: '#2D2F7E' }}>
                        {tr('Privacy Notice:', 'Avviso privacy:')}
                      </strong>{' '}
                      {tr(
                        'We only store this information in your private Petso profile. We do not perform registry lookups or contact external microchip databases.',
                        'Conserviamo queste informazioni solo nel tuo profilo Petso privato. Non effettuiamo ricerche nei registri né contattiamo database microchip esterni.'
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => setCurrentStep('education')}
                  className="flex-1 px-6 py-3 rounded-xl border-2 border-gray-300 hover:bg-gray-50 transition-colors"
                  style={{
                    color: '#464452',
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 700,
                  }}
                >
                  {tr('Back', 'Indietro')}
                </button>
                <button
                  onClick={() =>
                    setCurrentStep(hasDID ? 'link-did' : 'visibility')
                  }
                  disabled={!canProceedFromDetails()}
                  className="flex-1 px-6 py-3 rounded-xl text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={{
                    background:
                      'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 700,
                  }}
                >
                  {tr('Continue', 'Continua')}
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Link to Pet DID */}
          {currentStep === 'link-did' && hasDID && (
            <div className="space-y-6">
              <div>
                <h3
                  className="text-xl mb-2"
                  style={{
                    color: '#2D2F7E',
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 700,
                  }}
                >
                  {tr(
                    'Link to Pet DID (Optional)',
                    'Collega al DID dell’animale (opzionale)'
                  )}
                </h3>
                <p
                  style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}
                >
                  {tr(
                    `You can link the microchip ID to ${petName}'s Decentralized Identity (DID) to create a private reference.`,
                    `Puoi collegare l'ID microchip al DID di ${petName} per creare un riferimento privato.`
                  )}
                </p>
              </div>

              {/* Toggle Card */}
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                      <Link2 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4
                        className="font-semibold mb-1"
                        style={{
                          color: '#2D2F7E',
                          fontFamily: 'Sora, sans-serif',
                        }}
                      >
                        {tr(
                          'Link microchip ID to Pet DID',
                          'Collega ID microchip al DID'
                        )}
                      </h4>
                      <p
                        className="text-sm"
                        style={{
                          color: '#464452',
                          fontFamily: 'Inter, sans-serif',
                        }}
                      >
                        {tr(
                          'Create a private, encrypted reference between the microchip and blockchain identity',
                          'Crea un riferimento privato e crittografato tra microchip e identità blockchain'
                        )}
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={chipData.linkToDID}
                      onChange={(e) =>
                        updateChipData('linkToDID', e.target.checked)
                      }
                      className="sr-only peer"
                    />
                    <div className="w-14 h-8 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-blue-600 peer-checked:to-purple-600"></div>
                  </label>
                </div>

                {chipData.linkToDID && (
                  <div className="bg-white rounded-lg p-4 space-y-2">
                    <div
                      className="flex items-center gap-2 text-sm"
                      style={{
                        color: '#464452',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span>
                        {tr(
                          'Microchip reference will be encrypted',
                          'Il riferimento microchip sarà crittografato'
                        )}
                      </span>
                    </div>
                    <div
                      className="flex items-center gap-2 text-sm"
                      style={{
                        color: '#464452',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span>
                        {tr(
                          'Hashed reference attached to DID metadata',
                          'Riferimento hash collegato ai metadati DID'
                        )}
                      </span>
                    </div>
                    <div
                      className="flex items-center gap-2 text-sm"
                      style={{
                        color: '#464452',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span>
                        {tr(
                          'No data written to microchip or external registries',
                          'Nessun dato scritto sul microchip o su registri esterni'
                        )}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Explanation */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4
                      className="font-semibold mb-2"
                      style={{
                        color: '#2D2F7E',
                        fontFamily: 'Sora, sans-serif',
                      }}
                    >
                      {tr('What does this mean?', 'Cosa significa?')}
                    </h4>
                    <p
                      className="text-sm mb-2"
                      style={{
                        color: '#464452',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      {tr(
                        'Linking to the Pet DID creates a private, encrypted connection between the microchip number and the blockchain identity. This is stored only in your Petso account and can be used for verification purposes.',
                        'Il collegamento al DID crea una connessione privata e crittografata tra numero microchip e identità blockchain. È salvata solo nel tuo account Petso e può essere usata per verifiche.'
                      )}
                    </p>
                    <p
                      className="text-sm"
                      style={{
                        color: '#464452',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      <strong>{tr('Important:', 'Importante:')}</strong>{' '}
                      {tr(
                        'No data is written to the physical microchip, and external registries are not contacted.',
                        'Nessun dato viene scritto sul microchip fisico e i registri esterni non vengono contattati.'
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => setCurrentStep('enter-details')}
                  className="flex-1 px-6 py-3 rounded-xl border-2 border-gray-300 hover:bg-gray-50 transition-colors"
                  style={{
                    color: '#464452',
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 700,
                  }}
                >
                  {tr('Back', 'Indietro')}
                </button>
                <button
                  onClick={() => setCurrentStep('visibility')}
                  className="flex-1 px-6 py-3 rounded-xl text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                  style={{
                    background:
                      'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 700,
                  }}
                >
                  {tr('Continue', 'Continua')}
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Visibility & Access Controls */}
          {currentStep === 'visibility' && (
            <div className="space-y-6">
              <div>
                <h3
                  className="text-xl mb-2"
                  style={{
                    color: '#2D2F7E',
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 700,
                  }}
                >
                  {tr(
                    'Privacy & Access Controls',
                    'Privacy e controlli di accesso'
                  )}
                </h3>
                <p
                  style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}
                >
                  {tr(
                    'Choose who can see the microchip reference. You can change this anytime.',
                    'Scegli chi può vedere il riferimento microchip. Puoi modificarlo in qualsiasi momento.'
                  )}
                </p>
              </div>

              {/* Visibility Options */}
              <div className="space-y-3">
                {/* Only Me */}
                <label
                  className={`block p-5 rounded-xl border-2 cursor-pointer transition-all ${
                    chipData.visibility === 'only-me'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <input
                      type="radio"
                      name="visibility"
                      value="only-me"
                      checked={chipData.visibility === 'only-me'}
                      onChange={(e) =>
                        updateChipData('visibility', e.target.value)
                      }
                      className="mt-1 w-5 h-5 text-blue-600"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Lock className="w-5 h-5 text-blue-600" />
                        <h4
                          className="font-semibold"
                          style={{
                            color: '#2D2F7E',
                            fontFamily: 'Sora, sans-serif',
                          }}
                        >
                          {tr('Only Me (Recommended)', 'Solo io (consigliato)')}
                        </h4>
                      </div>
                      <p
                        className="text-sm"
                        style={{
                          color: '#464452',
                          fontFamily: 'Inter, sans-serif',
                        }}
                      >
                        {tr(
                          'Microchip reference is completely private. Only you can see it in your pet profile.',
                          'Il riferimento microchip è totalmente privato. Solo tu puoi vederlo nel profilo del tuo animale.'
                        )}
                      </p>
                    </div>
                  </div>
                </label>

                {/* Professionals I Book With */}
                <label
                  className={`block p-5 rounded-xl border-2 cursor-pointer transition-all ${
                    chipData.visibility === 'professionals'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <input
                      type="radio"
                      name="visibility"
                      value="professionals"
                      checked={chipData.visibility === 'professionals'}
                      onChange={(e) =>
                        updateChipData('visibility', e.target.value)
                      }
                      className="mt-1 w-5 h-5 text-blue-600"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Eye className="w-5 h-5 text-purple-600" />
                        <h4
                          className="font-semibold"
                          style={{
                            color: '#2D2F7E',
                            fontFamily: 'Sora, sans-serif',
                          }}
                        >
                          {tr(
                            'Professionals I Book With',
                            'Professionisti con cui prenoto'
                          )}
                        </h4>
                      </div>
                      <p
                        className="text-sm"
                        style={{
                          color: '#464452',
                          fontFamily: 'Inter, sans-serif',
                        }}
                      >
                        {tr(
                          'Veterinarians, groomers, and other professionals you book appointments with can see the microchip reference.',
                          'Veterinari, toelettatori e altri professionisti con cui prenoti possono vedere il riferimento microchip.'
                        )}{' '}
                        {tr(
                          'No medical data is shared automatically.',
                          'Nessun dato medico viene condiviso automaticamente.'
                        )}
                      </p>
                    </div>
                  </div>
                </label>

                {/* Emergency Access Only */}
                <label
                  className={`block p-5 rounded-xl border-2 cursor-pointer transition-all ${
                    chipData.visibility === 'emergency'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <input
                      type="radio"
                      name="visibility"
                      value="emergency"
                      checked={chipData.visibility === 'emergency'}
                      onChange={(e) =>
                        updateChipData('visibility', e.target.value)
                      }
                      className="mt-1 w-5 h-5 text-blue-600"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertCircle className="w-5 h-5 text-red-600" />
                        <h4
                          className="font-semibold"
                          style={{
                            color: '#2D2F7E',
                            fontFamily: 'Sora, sans-serif',
                          }}
                        >
                          {tr(
                            'Emergency Access Only',
                            'Solo accesso emergenza'
                          )}
                        </h4>
                      </div>
                      <p
                        className="text-sm"
                        style={{
                          color: '#464452',
                          fontFamily: 'Inter, sans-serif',
                        }}
                      >
                        {tr(
                          'Microchip reference is shown only in emergency situations via QR code or emergency contact sharing.',
                          'Il riferimento microchip è mostrato solo in situazioni di emergenza tramite QR o condivisione contatto emergenza.'
                        )}{' '}
                        {tr(
                          'Helps identify lost pets quickly.',
                          'Aiuta a identificare rapidamente animali smarriti.'
                        )}
                      </p>
                    </div>
                  </div>
                </label>
              </div>

              {/* What's Shared Notice */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4
                      className="font-semibold mb-2"
                      style={{
                        color: '#2D2F7E',
                        fontFamily: 'Sora, sans-serif',
                      }}
                    >
                      {tr(
                        "What's NOT automatically shared:",
                        'Cosa NON viene condiviso automaticamente:'
                      )}
                    </h4>
                    <ul
                      className="space-y-1 text-sm"
                      style={{
                        color: '#464452',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      <li>
                        •{' '}
                        {tr(
                          'Medical records and vaccination history',
                          'Cartelle mediche e storico vaccinazioni'
                        )}
                      </li>
                      <li>
                        •{' '}
                        {tr(
                          'Booking data or appointment history',
                          'Dati di prenotazione o storico appuntamenti'
                        )}
                      </li>
                      <li>
                        •{' '}
                        {tr(
                          'Social media posts or photos',
                          'Post social o foto'
                        )}
                      </li>
                      <li>
                        •{' '}
                        {tr(
                          'Contact information (unless emergency mode)',
                          'Informazioni di contatto (salvo modalità emergenza)'
                        )}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() =>
                    setCurrentStep(hasDID ? 'link-did' : 'enter-details')
                  }
                  className="flex-1 px-6 py-3 rounded-xl border-2 border-gray-300 hover:bg-gray-50 transition-colors"
                  style={{
                    color: '#464452',
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 700,
                  }}
                >
                  {tr('Back', 'Indietro')}
                </button>
                <button
                  onClick={() => setCurrentStep('confirmation')}
                  className="flex-1 px-6 py-3 rounded-xl text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                  style={{
                    background:
                      'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 700,
                  }}
                >
                  {tr('Review & Confirm', 'Rivedi e conferma')}
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Confirmation */}
          {currentStep === 'confirmation' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3
                  className="text-xl mb-2"
                  style={{
                    color: '#2D2F7E',
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 700,
                  }}
                >
                  {tr('Review & Confirm', 'Rivedi e conferma')}
                </h3>
                <p
                  style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}
                >
                  {tr(
                    'Please review the information before linking',
                    'Rivedi le informazioni prima del collegamento'
                  )}
                </p>
              </div>

              {/* Summary Card */}
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-6 space-y-4">
                <div>
                  <div
                    className="text-sm mb-1"
                    style={{
                      color: '#464452',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    {tr('Microchip Number', 'Numero microchip')}
                  </div>
                  <div
                    className="font-mono"
                    style={{
                      color: '#2D2F7E',
                      fontFamily: 'Sora, sans-serif',
                      fontWeight: 700,
                      fontSize: '18px',
                    }}
                  >
                    {chipData.chipNumber?.replace(
                      /(\d{3})(\d{3})(\d{3})(\d{3})/,
                      '$1 $2 $3 $4'
                    )}
                  </div>
                </div>

                {chipData.standard && (
                  <div>
                    <div
                      className="text-sm mb-1"
                      style={{
                        color: '#464452',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      {tr('Standard', 'Standard')}
                    </div>
                    <div
                      style={{
                        color: '#2D2F7E',
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      {chipData.standard}
                    </div>
                  </div>
                )}

                {chipData.implantDate && (
                  <div>
                    <div
                      className="text-sm mb-1"
                      style={{
                        color: '#464452',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      {tr('Implant Date', 'Data impianto')}
                    </div>
                    <div
                      style={{
                        color: '#2D2F7E',
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      {new Date(chipData.implantDate).toLocaleDateString()}
                    </div>
                  </div>
                )}

                <div className="pt-3 border-t border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className="text-sm"
                      style={{
                        color: '#464452',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      {tr('Link to Pet DID', 'Collega al DID')}
                    </span>
                    <span
                      className="font-semibold"
                      style={{
                        color: chipData.linkToDID ? '#10B981' : '#464452',
                        fontFamily: 'Sora, sans-serif',
                      }}
                    >
                      {chipData.linkToDID ? tr('Yes', 'Sì') : tr('No', 'No')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span
                      className="text-sm"
                      style={{
                        color: '#464452',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      {tr('Visibility', 'Visibilità')}
                    </span>
                    <span
                      className="font-semibold"
                      style={{
                        color: '#2D2F7E',
                        fontFamily: 'Sora, sans-serif',
                      }}
                    >
                      {chipData.visibility === 'only-me' &&
                        tr('Only Me', 'Solo io')}
                      {chipData.visibility === 'professionals' &&
                        tr('Professionals', 'Professionisti')}
                      {chipData.visibility === 'emergency' &&
                        tr('Emergency Only', 'Solo emergenza')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Final Disclaimer */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div
                    className="text-sm"
                    style={{
                      color: '#464452',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    {tr(
                      "By confirming, you acknowledge that this microchip link is for identification reference only and does not replace or modify your official microchip registry. You can unlink or modify this information at any time from your pet's profile.",
                      "Confermando, riconosci che questo collegamento microchip è solo un riferimento identificativo e non sostituisce né modifica il registro microchip ufficiale. Puoi scollegare o modificare queste informazioni in qualsiasi momento dal profilo dell'animale."
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => setCurrentStep('visibility')}
                  className="flex-1 px-6 py-3 rounded-xl border-2 border-gray-300 hover:bg-gray-50 transition-colors"
                  style={{
                    color: '#464452',
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 700,
                  }}
                >
                  {tr('Back', 'Indietro')}
                </button>
                <button
                  onClick={handleComplete}
                  className="flex-1 px-6 py-3 rounded-xl text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                  style={{
                    background:
                      'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 700,
                  }}
                >
                  <CheckCircle className="w-5 h-5" />
                  {tr('Confirm & Link', 'Conferma e collega')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
